import * as React from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

interface PendingState {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingState | null>(null);

  const confirm = React.useCallback<ConfirmContextValue['confirm']>((opts) => {
    const options: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const handleClose = (value: boolean) => {
    if (pending) {
      pending.resolve(value);
      setPending(null);
    }
  };

  const value = React.useMemo(() => ({ confirm }), [confirm]);

  const options = pending?.options;
  const confirmLabel = options?.confirmLabel ?? 'Confirm';
  const cancelLabel = options?.cancelLabel ?? 'Cancel';

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={!!pending} onClose={() => handleClose(false)} size="md">
        <DialogHeader title={options?.title ?? 'Please confirm'} onClose={() => handleClose(false)} />
        <DialogBody>
          <p className="text-sm text-muted-foreground">{options?.message}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            {cancelLabel}
          </Button>
          <Button variant={options?.variant === 'danger' ? 'destructive' : 'default'} onClick={() => handleClose(true)} autoFocus>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
